import { DynamicInputIcons } from "~/enums/dynamicInputType";
import {CertificateDynamicInputType} from "../../formability/certificateBuilder/enums/certificateDynamicInputType";
import Guid from "~/helpers/guid";
import { ObjectHelper } from "~/helpers/objectHelper";
import { Hash } from "~/interfaces/hashInterfaces";
import IRepo from "~/interfaces/repo";
import DynamicInputFactory from "~/models/dynamicInputFactory";
import { RelationType } from "~/models/relationType";
import UntrackedModel from "~/models/untracked/untrackedModel";
import Repo from "~/state/modules/repo";
import { ModelType } from '~/enums/modelType'
import { IImposter, IImposterOptions, IImposterFactory, IImposterMap } from '~/interfaces/imposterInterfaces'
import Imposter from '~/models/imposter'
import { GenericType } from '~/interfaces/genericTypeInterfaces'
import { Logger } from '~/services/logger'
import { commonImposterMap } from '~/models/imposterMapFactories/commonImposterMap'
import { flatten, flattenDeep } from "lodash";
import {ReferenceNodeType} from "~/enums/referenceNodeType";


export default class DynamicInput extends UntrackedModel implements IImposterFactory {

  static repo: IRepo<DynamicInput> = new Repo<DynamicInput>('dynamicInputs', DynamicInput);

  client_id: string;
  name: string = "";
  type: number = CertificateDynamicInputType.Text;
  sort_order: number = 0;
  is_list: boolean = false;
  is_optional: boolean = false;
  static_value: string = ""
  certificate_dynamic_inputs: Hash<DynamicInput> = {};

  except = ['id']

  get certificateDynamicInputs(): DynamicInput[] {
    return ObjectHelper.toArray(this.certificate_dynamic_inputs)
      .sort((a, b) => a.sort_order - b.sort_order);
  }

  get nextDynamicInputSortOrder(): number {
    const inputs = this.certificateDynamicInputs;
    if (inputs.length === 0) return 0;
    else return inputs[inputs.length - 1].sort_order + 1;
  }

  recalculateDynamicInputPositions() {
    this.certificateDynamicInputs.forEach((it, index) => it.sort_order = index);
  }

  get isDynamic() {
    return true;
  }

  get rootName(): string {
    return 'certificate_dynamic_inputs';
  }

  createEmbeddedObject = {
    certificate_dynamic_inputs: (parent, json) => {
      return DynamicInputFactory.create(this, json, false, true);
    },
  };

  get EMBEDDED_MODELS(): { [p: string]: { type: any; relationType: RelationType } } {
    return {
      'certificate_dynamic_inputs': {type: DynamicInput, relationType: RelationType.dictionary},
    };
  }

  definePropertiesFrom(parent: DynamicInput, json: object) {
    this.client_id = json["client_id"] || Guid.create()
    this.name = json['name'] || this.name
    this.type = json['type'] || this.type
    this.sort_order = json['sort_order'] || this.sort_order
    this.is_list = json['is_list'] || this.is_list
    this.is_optional = json['is_optional'] || this.is_optional
    this.static_value = json['static_value'] || this.static_value
  }

  afterDestroy() {
    super.afterDestroy();
    this.parent.recalculateDynamicInputPositions();
  }

  get typeDisplay(): string {
    const name = CertificateDynamicInputType[this.type];
    if (this.is_list) {
      return name + ' List';
    } else {
      return name;
    }
  }

  get typeIcon(): string {
    return DynamicInputIcons[this.type];
  }

  getDisplayName(): string {
    return "Dynamic Inputs"
  }

  get isList(): boolean {
    return this.is_list || this.type === CertificateDynamicInputType.Repeatable;
  }

  /**
   * if the data of interest is itself, a collection of data
   */
  get isDataCollection(): boolean {
    return this.isList
  }

  extractAllObjects(): DynamicInput[] {
    let objects: DynamicInput[] = [];

    if (this.type === CertificateDynamicInputType.Object) {
      objects.push(this);
    }

    const nestedObjects = flattenDeep(this.certificateDynamicInputs.map(dynamicInput => dynamicInput.extractAllObjects()))
    
    return objects.concat(nestedObjects);
  }

  extractAllInputs(): DynamicInput[] {
    let objects: DynamicInput[] = [];
    objects.push(this);
    const nestedInputs = flattenDeep(this.certificateDynamicInputs.map(dynamicInput => dynamicInput.extractAllInputs()))
    
    return objects.concat(nestedInputs);
  }

  imposterFor({ modelType, options }: { modelType: ModelType; options?: IImposterOptions }): IImposter {
    let map = this.imposterMapFor({ modelType, options })
    if (!map) {
      map = {
        id: this.id,
        ...ObjectHelper.sliceProperties(this as DynamicInput, 'genericType', 'modelType'),
        isCollection: undefined, // set below
      }
    }

    if (options.hasOwnProperty('isCollection')) {
      map.isCollection = options.isCollection || this.isList;
    } else {
      map.isCollection = this.isList
    }

    return new Imposter(map, options)
  }

  imposterMapFor({ modelType, options }: { modelType: ModelType; options?: IImposterOptions }): IImposterMap {
    let source = {modelType: this.modelType, genericType: this.genericType}

    return commonImposterMap({
        destination: { modelType, genericType: options.genericType },
        source,
        options,
      },
    )
  }

  get genericType(): GenericType {
    switch (this.type) {
      case CertificateDynamicInputType.Static: // fallthrough
      case CertificateDynamicInputType.Text:
        return ModelType.Text
      case CertificateDynamicInputType.Number:
        return ModelType.Number
      case CertificateDynamicInputType.Object:
        return ModelType.Object
      case CertificateDynamicInputType.File:
        return ModelType.File
      default:
        Logger.errorOnce('DynamicInput', `CertificateDynamicInputType ${this.type} not implemented yet`)
    }
  }

  get modelType(): ModelType {
    return ModelType.DynamicInput
  }

  get referenceNodeType(): ReferenceNodeType {
    return ReferenceNodeType.DynamicInput;
  }

}